'use client';

import { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

const steps = [
  {
    n: '01',
    icon: '🛒',
    title: 'Choisissez votre service',
    desc: 'Sélectionnez le type de boost souhaité (abonnés, likes ou commentaires) et la quantité qui correspond à votre budget.',
    color: '#1877F2',
  },
  {
    n: '02',
    icon: '💳',
    title: 'Payez en toute sécurité',
    desc: 'Réglez votre commande via Orange Money, MTN MoMo, Wave ou carte bancaire. Paiement instantané en XOF.',
    color: '#F4A923',
  },
  {
    n: '03',
    icon: '🚀',
    title: 'Recevez vos résultats',
    desc: 'Votre commande est traitée automatiquement. Vous recevez vos abonnés/likes/commentaires en moins de 24h.',
    color: '#10B981',
  },
];

export default function HowItWorks() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.step-reveal').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 180);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 sm:py-28 px-4 sm:px-6 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <div className="section-label mb-5 mx-auto" style={{ display: 'inline-flex' }}>
            <Icon name="MapIcon" size={13} className="text-[#1877F2]" />
            Comment ça marche
          </div>
          <h2
            className="font-heading font-bold text-[#0D1B3E]"
            style={{ fontSize: 'clamp(1.8rem,4vw,3rem)', lineHeight: 1.15 }}
          >
            Simple comme{' '}
            <span className="gradient-text">1, 2, 3</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 relative">
          {steps.map((step, i) => (
            <div
              key={step.n}
              className="step-reveal relative"
              style={{ opacity: 0, transform: 'translateY(30px)', transition: 'all 0.7s cubic-bezier(0.16,1,0.3,1)' }}
            >
              {/* Connector line */}
              {i < steps.length - 1 && (
                <div
                  className="hidden md:block absolute top-7 left-[calc(50%+32px)] h-0.5"
                  style={{
                    width: 'calc(100% - 64px)',
                    background: `linear-gradient(90deg, ${step.color} 0%, rgba(13,27,62,0.1) 100%)`,
                    zIndex: 0,
                  }}
                />
              )}

              <div
                className="relative z-10 flex flex-col items-center text-center p-8 rounded-3xl"
                style={{ background: 'rgba(244,246,251,0.8)', border: '1px solid rgba(13,27,62,0.06)' }}
              >
                {/* Step number circle */}
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center text-2xl mb-5 relative"
                  style={{ background: `rgba(${step.color === '#1877F2' ? '24,119,242' : step.color === '#F4A923' ? '244,169,35' : '16,185,129'},0.1)`, border: `2px solid ${step.color}` }}
                >
                  {step.icon}
                  <span
                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                    style={{ background: step.color }}
                  >
                    {step.n.replace('0', '')}
                  </span>
                </div>

                <h3 className="font-heading font-bold text-[#0D1B3E] text-lg mb-3">{step.title}</h3>
                <p className="text-[#6B7280] text-sm leading-relaxed">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Guarantee strip */}
        <div
          className="mt-12 p-5 rounded-2xl flex flex-wrap items-center justify-center gap-6 text-sm font-medium"
          style={{ background: 'linear-gradient(135deg,rgba(24,119,242,0.05) 0%,rgba(244,169,35,0.05) 100%)', border: '1px solid rgba(24,119,242,0.1)' }}
        >
          {[
            { icon: '🛡️', text: 'Garantie satisfait ou remboursé' },
            { icon: '🔒', text: 'Aucun mot de passe requis' },
            { icon: '🇨🇮', text: 'Support WhatsApp en français' },
            { icon: '⚡', text: 'Livraison automatique 24/7' },
          ].map((g) => (
            <div key={g.text} className="flex items-center gap-2 text-[#374151]">
              <span>{g.icon}</span>
              <span>{g.text}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}